/**
 * Apply-to-profile field mappings — defines how deliverable data maps to coach profile fields.
 *
 * Shared between client (for rendering the review UI) and server (for applying).
 * Each agent type defines its own mapping config. The apply infrastructure is generic.
 *
 * Aliases: apply mappings, deliverable mappings, profile field mappings
 */

/** Keys returned by the LLM adaptation step. */
export type AdaptedFieldKey =
  | "coachingTitle"
  | "openingStatement"
  | "coachingStatement"
  | "profileDescription"
  | "whyTitle"
  | "whyContent"
  | "challengesHeadline"
  | "challengesDescription"
  | "outcomesHeadline"
  | "outcomesDescription"
  | "offerPreHeadline"
  | "offerHeadline"
  | "offerDescription"
  | "commonChallenges";

export type AdaptedFields = Record<AdaptedFieldKey, string>;

export type ApplyTargetTable = "profiles" | "coachDetails" | "offers";

export type ApplyTargetGroup = "profile" | "details" | "offer";

export interface ApplyFieldMapping {
  /** Unique key for this field, e.g. "profile.coachingTitle" */
  key: string;
  /** Key into the AdaptedFields object from the LLM adaptation step */
  adaptedKey: AdaptedFieldKey;
  /** Human-readable label shown in the review UI */
  label: string;
  /** Which target group this belongs to (drives section grouping in UI) */
  group: ApplyTargetGroup;
  /** Database table to write to */
  targetTable: ApplyTargetTable;
  /** Column name in the target table */
  targetColumn: string;
  /** Description shown below the field label, includes constraints */
  description?: string;
  /** Max character length for this field */
  maxLength?: number;
}

export interface ApplyFieldGroup {
  key: ApplyTargetGroup;
  label: string;
  description: string;
}

export const APPLY_FIELD_GROUPS: ApplyFieldGroup[] = [
  {
    key: "profile",
    label: "Profile & Positioning",
    description: "Public-facing profile content visitors see",
  },
  {
    key: "offer",
    label: "Coaching Offer",
    description: "Your main coaching package",
  },
  {
    key: "details",
    label: "Matching Details",
    description: "Used to match you with the right clients",
  },
];

/**
 * ICP deliverable → coach profile field mappings.
 *
 * Values come from the LLM adaptation step (AdaptedFields), not raw deliverable data.
 * The `adaptedKey` maps to the field name in the adapted output.
 */
export const ICP_APPLY_MAPPINGS: ApplyFieldMapping[] = [
  // Profile & Positioning
  {
    key: "profile.coachingTitle",
    adaptedKey: "coachingTitle",
    label: "Coaching Title",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "coachingTitle",
    description:
      "Professional title shown below your name (e.g., 'Life Coach')",
    maxLength: 50,
  },
  {
    key: "profile.openingStatement",
    adaptedKey: "openingStatement",
    label: "Opening Statement",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "openingStatement",
    description: "Short quote that appears below your name",
    maxLength: 100,
  },
  {
    key: "profile.title",
    adaptedKey: "coachingStatement",
    label: "Coaching Statement",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "title",
    description: "'I help [who] to [achieve outcome]' format",
    maxLength: 150,
  },
  {
    key: "profile.description",
    adaptedKey: "profileDescription",
    label: "Profile Description",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "description",
    description: "Longer introduction about you and your coaching practice",
    maxLength: 400,
  },
  {
    key: "profile.whyTitle",
    adaptedKey: "whyTitle",
    label: "Approach Title",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "whyTitle",
    description:
      "Headline for the 04 · Approach section (e.g., 'To help one million people...')",
    maxLength: 100,
  },
  {
    key: "profile.whyContent",
    adaptedKey: "whyContent",
    label: "Approach Story",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "whyContent",
    description:
      "Body of the 04 · Approach section — personal story and motivation",
    maxLength: 500,
  },
  {
    key: "profile.challengesHeadline",
    adaptedKey: "challengesHeadline",
    label: "Challenges Headline",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "challengesHeadline",
    description:
      "Headline for the 02 · Challenges section (defaults to 'What I help clients work through' if empty)",
    maxLength: 100,
  },
  {
    key: "profile.challengesDescription",
    adaptedKey: "challengesDescription",
    label: "Challenges Description",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "challengesDescription",
    description:
      "Body of the 02 · Challenges section — the felt experience of being stuck, in the visitor's own voice",
    maxLength: 400,
  },
  {
    key: "profile.outcomesHeadline",
    adaptedKey: "outcomesHeadline",
    label: "Outcomes Headline",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "outcomesHeadline",
    description:
      "Headline for the 03 · Outcomes section (defaults to 'What clients walk away with' if empty)",
    maxLength: 100,
  },
  {
    key: "profile.outcomesDescription",
    adaptedKey: "outcomesDescription",
    label: "Outcomes Intro",
    group: "profile",
    targetTable: "profiles",
    targetColumn: "outcomesDescription",
    description:
      "Optional intro paragraph above the outcomes tiles — narrative texture for the section",
    maxLength: 400,
  },

  // Coaching Offer (drives the 05 · The coaching section)
  {
    key: "offer.preHeadline",
    adaptedKey: "offerPreHeadline",
    label: "Offer Eyebrow",
    group: "offer",
    targetTable: "offers",
    targetColumn: "preHeadline",
    description:
      "Short kicker above the offer headline (e.g., 'Cohort 04', 'Limited spots')",
    maxLength: 50,
  },
  {
    key: "offer.headline",
    adaptedKey: "offerHeadline",
    label: "Offer Headline",
    group: "offer",
    targetTable: "offers",
    targetColumn: "headline",
    description:
      "Title of your main offer (used as the 05 · The coaching section title)",
    maxLength: 100,
  },
  {
    key: "offer.description",
    adaptedKey: "offerDescription",
    label: "Offer Description",
    group: "offer",
    targetTable: "offers",
    targetColumn: "description",
    description: "What your coaching offer includes and delivers",
    maxLength: 300,
  },

  // Matching Details
  {
    key: "details.commonChallenges",
    adaptedKey: "commonChallenges",
    label: "Common Challenges",
    group: "details",
    targetTable: "coachDetails",
    targetColumn: "commonChallenges",
    description: "What your ideal clients struggle with",
  },
];

// ---------------------------------------------------------------------------
// Field-length helpers
//
// `ICP_APPLY_MAPPINGS[].maxLength` is the single source of truth for per-field
// limits. The LLM adaptation prompt, the profile-update tool validation, and
// the deterministic clamp below all derive from it so the limits can't drift.
// ---------------------------------------------------------------------------

/** Max length keyed by the LLM adaptation output key. Uncapped fields omitted. */
export const MAX_LENGTH_BY_ADAPTED_KEY: Partial<
  Record<AdaptedFieldKey, number>
> = ICP_APPLY_MAPPINGS.reduce<Partial<Record<AdaptedFieldKey, number>>>(
  (acc, m) => {
    if (m.maxLength != null) acc[m.adaptedKey] = m.maxLength;
    return acc;
  },
  {},
);

/** Max length for a mapping key (e.g. "profile.title"), or undefined if uncapped. */
export function getMaxLengthForKey(key: string): number | undefined {
  return ICP_APPLY_MAPPINGS.find((m) => m.key === key)?.maxLength;
}

/**
 * Rough word budget for a character limit. LLMs reason about words far more
 * reliably than characters, and the conservative divisor leaves headroom so a
 * model that lands near the word target still falls under the hard char limit.
 */
export function approxWordBudget(maxLength: number): number {
  return Math.max(1, Math.floor(maxLength / 6.5));
}

export interface FieldOverage {
  adaptedKey: AdaptedFieldKey;
  length: number;
  maxLength: number;
  over: number;
}

/** Adapted fields whose value exceeds the configured max length. */
export function findAdaptedFieldOverages(
  fields: Partial<Record<AdaptedFieldKey, string>>,
): FieldOverage[] {
  return Object.entries(MAX_LENGTH_BY_ADAPTED_KEY).flatMap(
    ([key, maxLength]) => {
      const adaptedKey = key as AdaptedFieldKey;
      const value = fields[adaptedKey];
      if (
        typeof value !== "string" ||
        maxLength == null ||
        value.length <= maxLength
      ) {
        return [];
      }
      return [
        {
          adaptedKey,
          length: value.length,
          maxLength,
          over: value.length - maxLength,
        },
      ];
    },
  );
}

/**
 * Hard-truncate to maxLength at a word boundary. Last-resort guard so a value
 * can never exceed its limit even if the model ignores every instruction — the
 * revise loop should make this fire only rarely.
 */
export function clampToWordBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const slice = value.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(" ");
  const cut =
    lastSpace > Math.floor(maxLength * 0.6) ? slice.slice(0, lastSpace) : slice;
  // Drop dangling whitespace/punctuation left by the cut.
  return cut.replace(/[\s.,;:!?—-]+$/u, "");
}

/** Clamp every capped adapted field to its limit; no-op when all are in bounds. */
export function clampAdaptedFields(fields: AdaptedFields): AdaptedFields {
  return Object.entries(MAX_LENGTH_BY_ADAPTED_KEY).reduce(
    (acc, [key, maxLength]) => {
      const adaptedKey = key as AdaptedFieldKey;
      const value = acc[adaptedKey];
      if (typeof value === "string" && maxLength != null) {
        acc[adaptedKey] = clampToWordBoundary(value, maxLength);
      }
      return acc;
    },
    { ...fields },
  );
}
